<script>
import LabelDropdownItem from './LabelDropdownItem.vue';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import { picoSearch } from '@chatwoot/pico-search';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';

// Seletor de etiquetas da conversa: busca com lupa, lista com a cor e a descrição de
// cada etiqueta, as marcadas destacadas e navegação por setas (Enter marca/desmarca).
export default {
  components: {
    LabelDropdownItem,
    Hotkey,
  },

  props: {
    accountLabels: {
      type: Array,
      default: () => [],
    },
    selectedLabels: {
      type: Array,
      default: () => [],
    },
    allowCreation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'add', 'remove', 'create'],

  data() {
    return {
      search: '',
      labelHotkey: 'L',
      activeIndex: 0,
    };
  },

  computed: {
    createLabelPlaceholder() {
      const label = this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
      return this.search ? `${label}:` : label;
    },

    filteredActiveLabels() {
      if (!this.search) return this.accountLabels;

      return picoSearch(this.accountLabels, this.search, ['title']);
    },

    noResult() {
      return this.filteredActiveLabels.length === 0;
    },

    hasExactMatchInResults() {
      return this.filteredActiveLabels.some(
        label => label.title === this.search
      );
    },

    shouldShowCreate() {
      return this.allowCreation && this.filteredActiveLabels.length < 3;
    },

    parsedSearch() {
      return sanitizeLabel(this.search);
    },

    selectedCount() {
      return this.selectedLabels.length;
    },
  },

  watch: {
    search() {
      this.activeIndex = 0;
    },
  },

  mounted() {
    this.focusInput();
  },

  methods: {
    focusInput() {
      this.$refs.searchbar.focus();
    },

    updateLabels(label) {
      this.$emit('update', label);
    },

    onAdd(label) {
      this.$emit('add', label);
    },

    onRemove(label) {
      this.$emit('remove', label);
    },

    onAddRemove(label) {
      if (this.selectedLabels.includes(label.title)) {
        this.onRemove(label.title);
      } else {
        this.onAdd(label);
      }
    },

    moveActive(step) {
      const total = this.filteredActiveLabels.length;
      if (!total) return;

      this.activeIndex = (this.activeIndex + step + total) % total;
      this.$nextTick(() => {
        this.$refs.list
          ?.querySelector(`[data-index="${this.activeIndex}"]`)
          ?.scrollIntoView?.({ block: 'nearest' });
      });
    },

    toggleActive() {
      const label = this.filteredActiveLabels[this.activeIndex];
      if (label) this.onAddRemove(label);
    },

    showCreateModal() {
      this.$emit('create', this.parsedSearch);
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-h-[22rem]" data-testid="label-dropdown">
    <header class="flex items-center gap-2 px-3 pt-3 pb-2">
      <span
        class="grid place-items-center size-7 shrink-0 rounded-lg bg-n-alpha-2 text-n-slate-11"
        aria-hidden="true"
      >
        <span class="i-hugeicons-tag-01 size-4" />
      </span>
      <div class="min-w-0 flex-1">
        <h4 class="m-0 truncate text-sm font-semibold text-n-slate-12">
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TITLE') }}
        </h4>
        <p
          class="m-0 text-xs text-n-slate-11"
          data-testid="label-dropdown-count"
        >
          {{
            selectedCount
              ? $t(
                  'CONTACT_PANEL.LABELS.LABEL_SELECT.SELECTED_COUNT',
                  { count: selectedCount },
                  selectedCount
                )
              : $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NONE_SELECTED')
          }}
        </p>
      </div>
      <Hotkey
        custom-class="border border-solid text-n-slate-11 bg-n-alpha-1 text-xxs border-n-weak flex-shrink-0"
      >
        {{ labelHotkey }}
      </Hotkey>
    </header>

    <div class="px-3 pb-2">
      <label class="relative block">
        <span class="sr-only">
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER') }}
        </span>
        <span
          class="i-hugeicons-search-01 pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          aria-hidden="true"
        />
        <input
          ref="searchbar"
          v-model="search"
          type="text"
          autofocus="true"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="true"
          class="!mb-0 !h-9 w-full !rounded-lg !border !border-n-weak !bg-n-alpha-1 !py-0 !pl-8 !pr-3 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:!border-n-brand focus:!outline-none focus:!ring-2 focus:!ring-n-brand/20"
          :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="toggleActive"
        />
      </label>
    </div>

    <div
      ref="list"
      role="listbox"
      aria-multiselectable="true"
      class="min-h-0 flex-1 overflow-y-auto border-t border-n-weak px-1.5 py-1.5"
    >
      <LabelDropdownItem
        v-for="(label, index) in filteredActiveLabels"
        :key="label.title"
        :data-index="index"
        :title="label.title"
        :color="label.color"
        :description="label.description"
        :selected="selectedLabels.includes(label.title)"
        :active="index === activeIndex"
        @mouseenter="activeIndex = index"
        @select-label="onAddRemove(label)"
      />
      <div
        v-if="noResult"
        class="flex flex-col items-center gap-1.5 px-3 py-6 text-center"
      >
        <span
          class="i-hugeicons-tag-01 size-5 text-n-slate-10"
          aria-hidden="true"
        />
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
        </span>
      </div>
    </div>

    <footer
      v-if="allowCreation && shouldShowCreate"
      class="border-t border-n-weak p-1.5"
    >
      <button
        type="button"
        class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-n-blue-11 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="hasExactMatchInResults"
        data-testid="label-dropdown-create"
        @click="showCreateModal"
      >
        <span class="i-hugeicons-add-01 size-4 shrink-0" aria-hidden="true" />
        <span class="truncate">
          {{ createLabelPlaceholder }}
          <strong v-if="parsedSearch" class="font-semibold">
            {{ parsedSearch }}
          </strong>
        </span>
      </button>
    </footer>
  </div>
</template>
